import { useAuthStore } from '@base/stores/auth'
import { getSessionId } from '@base/services/session'
import type { AuthenticationResult } from '@azure/msal-browser'
import type { Account } from '@base/types/account'
import type { Msal } from '@base/types/msal'

export const msalService = async (msal: Msal) => {
  if (import.meta.server) return // Skip server-side rendering

  if (!msal.auth) {
    throw new Error('Msal auth configuration is missing')
  }

  const authStore = useAuthStore()
  const sessionId = getSessionId()

  const { acquireTokenSilent } = useMsal(msal)

  if (sessionId && !authStore.accessToken) {
    try {
      const response = await fetch('/api/session', { credentials: 'include' })
      const { accessToken, expiresAt } = await response.json()

      if (accessToken) {
        authStore.setToken(accessToken, expiresAt)
      }
    } catch (error) {
      console.error('Failed to fetch session data', error)
    }
  }

  if (!authStore.accessToken || !authStore.account?.id) {
    try {
      const tokenResponse = (await acquireTokenSilent()) as AuthenticationResult | null | undefined

      if (tokenResponse?.accessToken) {
        const { accessToken, expiresOn, account } = tokenResponse
        const expiry = expiresOn?.getTime() ?? null

        authStore.setToken(tokenResponse.accessToken, expiry)

        if (account) {
          const accountInfo: Account = {
            id: account.localAccountId,
            name: account.name ?? '',
            username: account.username,
            email: account.idTokenClaims?.email as string,
            roles: account.idTokenClaims?.roles as string[]
          }
          authStore.setAccount(accountInfo)

          await fetch('/api/session', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ sessionId, accessToken }),
            credentials: 'include'
          })
        }
      }
    } catch (error) {
      console.error('An error occurred during middleware execution', error)
    }
  }
}
